import { BookOpen, TrendingUp, Shield, Target, Lightbulb, Users } from 'lucide-react';

export default function Resources() {
  const resources = [
    {
      icon: <TrendingUp size={48} />,
      title: 'Understanding Market Cycles',
      category: 'Market Analysis',
      content: 'Real estate markets move in predictable cycles. Understanding where we are in the cycle can help you make informed investment decisions. Key indicators include inventory levels, days on market, price trends, and new construction activity. During expansion phases, demand outpaces supply and prices rise. At peak, competition is highest. During contraction, inventory builds and prices stabilize or decline. Savvy investors position themselves to capitalize on each phase.',
      tips: [
        'Monitor local market indicators monthly',
        'Diversify across different property types',
        'Build cash reserves for opportunity purchases',
        'Work with experienced advisors who understand cycles'
      ]
    },
    {
      icon: <Shield size={48} />,
      title: 'Risk Assessment in Real Estate',
      category: 'Risk Management',
      content: 'Successful real estate investment requires thorough risk evaluation. Key risk factors include market risk, property-specific issues, financing terms, tenant quality, and economic conditions. Conducting comprehensive due diligence is essential. This includes property inspections, environmental assessments, title searches, market studies, and financial analysis. A diversified portfolio and conservative leverage ratios help mitigate risk.',
      tips: [
        'Always conduct thorough due diligence',
        'Maintain appropriate insurance coverage',
        'Keep debt levels conservative',
        'Plan for vacancy and unexpected expenses'
      ]
    },
    {
      icon: <Target size={48} />,
      title: 'Maximizing Property Value',
      category: 'Value Creation',
      content: 'Property value can be enhanced through strategic improvements, operational efficiency, and market positioning. Focus on improvements that offer the highest return on investment. These often include curb appeal enhancements, modernized interiors, and amenity upgrades. Efficient property management reduces operating costs and increases net operating income. Professional marketing and tenant selection maximize occupancy and rental rates.',
      tips: [
        'Prioritize improvements with highest ROI',
        'Maintain properties proactively, not reactively',
        'Position properties competitively in the market',
        'Track and optimize operating expenses'
      ]
    },
    {
      icon: <Lightbulb size={48} />,
      title: 'Emerging Investment Opportunities',
      category: 'Investment Strategy',
      content: 'The real estate landscape constantly evolves, creating new opportunities. Current trends include adaptive reuse of commercial properties, mixed-use developments, sustainable building practices, and technology-enhanced properties. Demographic shifts drive demand for specific property types. Understanding these trends helps identify emerging opportunities before they become obvious to the broader market.',
      tips: [
        'Stay informed about demographic and economic trends',
        'Consider properties positioned for future growth',
        'Evaluate sustainability and technology features',
        'Look for undervalued assets in transitioning markets'
      ]
    },
    {
      icon: <BookOpen size={48} />,
      title: 'Financing Strategies for Success',
      category: 'Financing',
      content: 'Strategic financing can dramatically impact investment returns. Options range from traditional mortgages to creative structures involving multiple capital sources. Understanding loan-to-value ratios, debt service coverage, interest rates, and terms is crucial. For larger or complex projects, combining senior debt, mezzanine financing, and equity from strategic partners may be optimal. Pre-qualified financing provides competitive advantage.',
      tips: [
        'Maintain strong relationships with multiple lenders',
        'Understand all financing options available',
        'Structure debt to match project timelines',
        'Consider both cost and flexibility when choosing financing'
      ]
    },
    {
      icon: <Users size={48} />,
      title: 'Building Strategic Partnerships',
      category: 'Relationships',
      content: 'Real estate success often depends on strong professional relationships. A network of trusted advisors, investors, contractors, and service providers provides competitive advantages. Strategic partnerships can provide capital, expertise, market access, and operational efficiency. Building and maintaining these relationships requires professionalism, integrity, and mutual benefit. The right partners can make challenging projects feasible.',
      tips: [
        'Invest time in building genuine relationships',
        'Provide value to your network consistently',
        'Seek partners whose strengths complement yours',
        'Maintain transparent and professional communication'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="resources-page">
      <section className="py-24 md:py-32 bg-[#020617]" data-testid="resources-hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h1 className="text-5xl md:text-7xl font-medium tracking-tight leading-none text-[#f8fafc] mb-8" data-testid="resources-title">
            Investment <span className="text-[#d4af37]">Resources</span>
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-slate-300 max-w-3xl mx-auto">
            Insights and strategies from our three decades of real estate investment experience
          </p>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#0f172a]" data-testid="resources-content-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="space-y-16">
            {resources.map((resource, index) => (
              <div
                key={index}
                data-testid={`resource-${index}`}
                className="glass-morphism p-8 md:p-12 hover:border-[#d4af37]/30 transition-all duration-500"
              >
                <div className="flex items-start space-x-6 mb-6">
                  <div className="text-[#d4af37] flex-shrink-0">
                    {resource.icon}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm tracking-widest uppercase text-[#d4af37] mb-3">
                      {resource.category}
                    </div>
                    <h2 className="text-3xl md:text-4xl font-normal text-[#f8fafc] mb-6">
                      {resource.title}
                    </h2>
                  </div>
                </div>
                
                <p className="text-base leading-relaxed text-slate-300 mb-8 ml-0 md:ml-20">
                  {resource.content}
                </p>
                
                <div className="ml-0 md:ml-20">
                  <h3 className="text-lg font-medium text-[#d4af37] mb-4">Key Takeaways</h3>
                  <div className="space-y-3">
                    {resource.tips.map((tip, tipIndex) => (
                      <div key={tipIndex} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-[#d4af37] rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-base text-slate-400">{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#020617]" data-testid="consultation-cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="glass-morphism p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-normal text-[#f8fafc] mb-6">
              Need Personalized Guidance?
            </h2>
            <p className="text-lg leading-relaxed text-slate-300 mb-8">
              These resources provide general insights, but every investment situation is unique. Our experienced team can provide tailored advice for your specific goals and circumstances.
            </p>
            <a
              href="/contact"
              className="inline-block bg-[#d4af37] text-[#020617] hover:bg-[#b5952f] rounded-sm px-8 py-4 font-medium transition-all duration-300 hover:scale-105"
              data-testid="schedule-consultation-cta-button"
            >
              Schedule a Consultation
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}